import PieChartElement, { PieChartData } from '@/views/elements/chart/PieChartElement';
import TypographyElement from '@/views/elements/typography/TypographyElement';
import classNames from 'classnames/bind';
import styles from './AllocationCategoryChartRealtime.module.scss';
import './InvestmentZoningChart.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import SymbolVirtualRow from '../HoldingsListSymbolRealtime/SymbolVirtualRow';
import { Box, Stack } from '@mui/material';
import { useGetIndustry, useGetLanguage } from '@/controllers/gridManager/appManager/appManager';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';

const cx = classNames.bind(styles);

type Props = {
    dataSeries: any
    dataReady: boolean
}

const NUMBER_SHOW_CHART = 5

export default function AllocationCategoryChartRealtime({
    dataSeries, dataReady
}: Props) {
    const { t } = useTranslation()
    const { language } = useGetLanguage()
    const [type, setType] = useState(0)
    const [cvDataSeries, setCvDataSeries] = useState<PieChartData>([])
    const originDataSeries = useRef<any[]>([])
    const [listSymbol, setListSymbol] = useState<string[]>([])
    const { industry } = useGetIndustry()

    // calc proportionAdded and limit
    const prepareDataForChartAllocPortfolios = (raw: any, findData?: any, marketPrice?: any) => {
        const cloneRaw = cloneDeep(raw)

        const dataForChart: PieChartData = []
        const Other = { name: "Khác", value: 0 }
        const Money = { name: "Tiền", value: 0 }
        let sumTotalSecuritiesOnMarket = 0

        const rs = cloneRaw.map((item: any) => {
            return { ...item, marketprice: item.symbol === findData?.symbol ? marketPrice : item.marketprice, name: item.symbol, value: (item.symbol === findData?.symbol ? marketPrice : item.marketprice) * item.total_trade, baseValue: item.costprice * item.total_trade }
        })

        for (let index = 0; index < rs.length; index++) {
            const element = rs[index];
            sumTotalSecuritiesOnMarket += Number.parseFloat(element.value)
        }
        const addProportion = rs.map((item: any) => {
            return { ...item, proportionAdded: item.value / sumTotalSecuritiesOnMarket }
        })

        addProportion.sort((a: any, b: any) => b.proportionAdded - a.proportionAdded)

        originDataSeries.current = addProportion
        // console.log('ha_check_originDataSeries1', { addProportion, originDataSeries: originDataSeries.current })

        let count = 0
        for (let index = 0; index < addProportion.length; index++) {
            const element = addProportion[index];
            if (element?.symbol === "Tiền") {
                Money.value = Money.value + parseFloat(element?.proportionAdded)
            } else if (count < NUMBER_SHOW_CHART && element?.proportionAdded) {
                count += 1
                dataForChart.push({
                    value: element?.proportionAdded,
                    name: element?.symbol
                })
            } else if (element?.proportionAdded && element?.proportionAdded !== 0) {
                Other.value = Other.value + parseFloat(element?.proportionAdded)
            }
        }

        if (Money && Money.value !== 0) {
            dataForChart.push(Money)
        }

        if (Other && Other.value !== 0) {
            dataForChart.push(Other)
        }

        // console.log('ha_check_dataForChart', { dataForChart, Money, language })


        return dataForChart
    }

    const prepareDataForChartIndustryAllocPortfolios = (raw: any) => {
        const cloneRaw = cloneDeep(raw)
        cloneRaw.sort((a: any, b: any) => b.value - a.value)
        const dataForChart: { name: string, value: number }[] = []
        const Other = { name: "Khác", value: 0 }
        const Money = { name: "Tiền", value: 0 }

        let count = 0
        for (let index = 0; index < cloneRaw.length; index++) {
            const element = cloneRaw[index];
            if (element?.name === "Tiền") {
                Money.value = Money.value + parseFloat(element?.value)
            } else if (element?.value && count < NUMBER_SHOW_CHART) {
                count += 1
                dataForChart.push(element)
            } else if (element?.value && element?.value !== 0) {
                // console.log('ha_check_elementOther', element)
                Other.value = Other.value + parseFloat(element?.value)
            }
        }

        if (Money && Money.value !== 0) {
            dataForChart.push(Money)
        }

        if (Other && Other.value !== 0) {
            dataForChart.push(Other)
        }
        return dataForChart
    }

    useEffect(() => {
        if (dataReady) {
            setCvDataSeries(prepareDataForChartAllocPortfolios(dataSeries))
            setListSymbol(dataSeries.map((item: any) => item.symbol))
        }
    }, [dataReady, dataSeries, language])


    const result = industry.map((subIndustry: any) => {
        const matchedCodes = listSymbol.filter((code: any) => subIndustry?.codeList?.includes(code));
        return {
            industryNameEn: subIndustry.industryNameEn,
            industryName: subIndustry.industryName,
            matchedCodes: matchedCodes
        };
    });

    const filteredResult = result.filter((item: any) => item.matchedCodes.length > 0);
    const filterWithoutMoney = listSymbol.filter((code: any) => code !== 'Tiền')

    const unmatchedCodes = filterWithoutMoney.filter((code: any) => (!result.some((subIndustry: any) => subIndustry.matchedCodes.includes(code))));

    // console.log('ha_check_result', { result, filteredResult, filterWithoutMoney, unmatchedCodes })

    const findMoney = cvDataSeries.find((item: any) => item.name === 'Tiền')

    if (findMoney && findMoney.value !== 0) {
        filteredResult.push({
            industryName: "Tiền",  // Ngành khác
            industryNameEn: "Money",
            matchedCodes: ["Tiền"]
        });
    }

    if (unmatchedCodes.length > 0) {
        filteredResult.push({
            industryName: "Khác",  // Ngành khác
            industryNameEn: "Other",
            matchedCodes: unmatchedCodes
        });
    }

    const data4Industry = filteredResult.map((item: any) => {
        let sum = 0
        for (let index = 0; index < cvDataSeries.length; index++) {
            const element = cvDataSeries[index];
            if (item?.matchedCodes.includes(element.name)) {
                sum += element.value
            }
        }
        return { name: language === "vi" ? item.industryName : item.industryNameEn, value: sum }
    })

    const buildData4Industry = prepareDataForChartIndustryAllocPortfolios(data4Industry)

    const applyTransactionAsyncRealtime = useCallback((newInstrument: any, log: string) => {
        // console.log('ha_check_applyTransactionAsyncRealtime1', newInstrument)

        const findData: any = originDataSeries.current.find((item: any) => {
            return (item?.symbol && newInstrument?.SB && (item?.symbol === newInstrument?.SB))
        })

        if (findData) {
            const marketPrice = newInstrument?.CP === 0 ? findData.marketprice : newInstrument?.CP ?? findData.marketprice
            setCvDataSeries(prepareDataForChartAllocPortfolios(originDataSeries.current, findData, marketPrice))
        }
    }, [dataSeries])

    // console.log('ha_check_data4Industry', data4Industry)
    // console.log('ha_check_originDataSeries', originDataSeries.current.map((item => `${item.marketprice} - ${item.symbol}`)))



    return (
        <Stack spacing={"20px"} p={"20px"} direction="column" className={cx("pie-chart", "full-width")}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <TypographyElement FontWeightElement={700} FontSizeElement='16px' lineHeight='24px'>
                    {t('copytrade.chart.allocation.title')}
                </TypographyElement>

                <Stack className={cx("allocation__switch")} direction='row'>
                    <Box className={cx("allocation__switch-thumb")} style={{ left: `${type * 65}px`, width: `calc(50% + ${type === 0 ? 5 : -5}px)` }}></Box>
                    <TypographyElement onClick={() => setType(0)} className={cx("allocation__switch-content")}>{t('copytrade.chart.allocation.instrument')}</TypographyElement>
                    <TypographyElement onClick={() => setType(1)} className={cx("allocation__switch-content")}>{t('copytrade.chart.allocation.industry')}</TypographyElement>
                </Stack>
            </Stack>

            <div className={cx("img-pie-chart")}>
                {dataReady && <PieChartElement
                    dataSeries={type === 0 ? cvDataSeries : buildData4Industry}
                    isLongLabel={type === 1}
                />}
            </div>
            {
                dataReady && dataSeries && dataSeries.map((item: any, index: number) => { //rowVirtualVisible
                    return (
                        <SymbolVirtualRow
                            keyChange='CP'
                            symbol={item?.symbol}
                            key={index}
                            applyTransactionAsyncRealtime={applyTransactionAsyncRealtime}
                        />
                    )
                })
            }
        </Stack>
    )
}